import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FaTree, FaBalanceScale, FaTimes } from "react-icons/fa";
import { FaHouseFloodWater, FaTemperatureArrowUp, FaSeedling } from "react-icons/fa6";
import { MapContainer } from '../components/Map/MapContainer';
import { 
  Header, 
  LayerPanel, 
  TreeCarousel
} from '../components/ui';
import type { 
  LayerControlData, 
  TreeCardData
} from '../components/ui';
import type { TreeSpecies, GeocodeResult } from '../types';
import { api, queryKeys } from '../lib/api';
import styles from './HomePage.module.css';

export const HomePage: React.FC = () => {
  // Selected address state
  const [selectedAddress, setSelectedAddress] = useState<{ longitude: number; latitude: number; address: string } | null>(null);

  // Layer data matching mockup
  const [layers, setLayers] = useState<LayerControlData[]>([
    {
      id: 'heatIslands',
      name: 'Heat Islands',
      icon: <FaTemperatureArrowUp />,
      description: 'Temperature hotspots and cooling priorities',
      isActive: true,
      isVisible: true,
      transparency: 60,
      order: 0,
      filters: {
        primary: 'All temperatures',
        secondary: 'All priority levels'
      }
    },
    {
      id: 'floodZones',
      name: 'Flood Zones',
      icon: <FaHouseFloodWater />,
      description: 'FEMA flood risk areas',
      isActive: false,
      isVisible: true,
      transparency: 50,
      order: 1,
      filters: {
        primary: 'All flood zones',
        secondary: 'All frequencies'
      }
    },
    {
      id: 'environmentalJustice',
      name: 'Environmental Justice',
      icon: <FaBalanceScale />,
      description: 'Priority communities for tree benefits',
      isActive: false,
      isVisible: true,
      transparency: 70,
      order: 2,
      filters: {
        primary: 'All EJ scores (0-10)',
        secondary: 'All demographics'
      }
    },
    {
      id: 'communityTrees',
      name: 'Community Trees',
      icon: <FaTree />,
      description: 'Planted trees in the community',
      isActive: true,
      isVisible: true,
      transparency: 100,
      order: 4,
      filters: {
        primary: 'All trees',
        secondary: 'All species'
      }
    }
  ]);

  // Fetch tree species from API
  const { data: treeSpeciesData, error } = useQuery({
    queryKey: queryKeys.treeSpecies,
    queryFn: api.getTreeSpecies,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Only retry once on failure
  });

  // Fetch community trees from API
  const { data: communityTreesData } = useQuery({
    queryKey: queryKeys.communityTrees(),
    queryFn: () => api.getCommunityTrees(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  // React Query client for cache invalidation
  const queryClient = useQueryClient();

  // Mutation for creating new community trees
  const createTreeMutation = useMutation({
    mutationFn: api.createCommunityTree,
    onSuccess: () => {
      // Invalidate and refetch community trees
      queryClient.invalidateQueries({ queryKey: queryKeys.communityTrees() });
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        createTreeMutation.reset();
      }, 3000);
    },
    onError: () => {
      // Auto-hide error message after 5 seconds
      setTimeout(() => {
        createTreeMutation.reset();
      }, 5000);
    },
  });

  // Transform TreeSpecies data to TreeCardData format
  const treeSpecies: TreeCardData[] = useMemo(() => {
    if (error || !treeSpeciesData || !Array.isArray(treeSpeciesData)) {
      return [];
    }
    
    return treeSpeciesData.map((species: TreeSpecies) => ({
      id: species.id.toString(),
      name: species.name,
      image: species.image_url || `/images/tree/${species.name.toLowerCase().replace(/\s+/g, '-')}.png`,
      stats: {
        height: `${species.max_height_feet}ft`,
        co2Absorption: `${species.carbon_sequestration_lbs_year} lbs/year`,
        tolerance: `${species.urban_tolerance.charAt(0).toUpperCase() + species.urban_tolerance.slice(1)} urban tolerance`,
        growthRate: species.growth_rate,
        lifespan: `${species.typical_lifespan_years} years`,
      },
      dragIndicator: true
    }));
  }, [treeSpeciesData, error]);


  // Event handlers
  const handleLayerToggle = useCallback((layerId: string) => {
    setLayers(prev => prev.map(layer => 
      layer.id === layerId 
        ? { ...layer, isActive: !layer.isActive }
        : layer
    ));
  }, []);

  const handleVisibilityToggle = useCallback((layerId: string) => {
    setLayers(prev => prev.map(layer => 
      layer.id === layerId 
        ? { ...layer, isVisible: !layer.isVisible }
        : layer
    ));
  }, []);

  const handleFilterChange = useCallback((layerId: string, filterType: string, value: string) => {
    setLayers(prev => prev.map(layer => 
      layer.id === layerId 
        ? { ...layer, filters: { ...layer.filters, [filterType]: value } }
        : layer
    ));
  }, []);

  const handleTransparencyChange = useCallback((layerId: string, transparency: number) => {
    setLayers(prev => prev.map(layer => 
      layer.id === layerId 
        ? { ...layer, transparency }
        : layer
    ));
  }, []);

  const handleLayerReorder = useCallback((reorderedLayers: LayerControlData[]) => {
    setLayers(reorderedLayers);
  }, []);

  const handleAddressSelect = useCallback((result: GeocodeResult | null) => {
    if (result) {
      setSelectedAddress({
        longitude: result.longitude,
        latitude: result.latitude,
        address: result.address
      });
    } else {
      // Clear the selected address
      setSelectedAddress(null);
    }
  }, []);

  const handleTreeSelect = useCallback(() => {
    // Handle tree selection logic here
  }, []);

  const handleTreeDrag = useCallback(() => {
    // Handle tree drag/drop logic here
  }, []);

  const handleTreeDrop = useCallback((longitude: number, latitude: number, treeData: TreeCardData) => {
    const speciesId = parseInt(treeData.id);
    
    if (isNaN(speciesId)) {
      return;
    }
    
    const newTreeData = {
      species_id: speciesId,
      longitude,
      latitude,
      address: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
      neighborhood: 'Unknown',
      planting_date: new Date().toISOString(),
      site_assessment_score: 50,
      ownership_type: 'community' as const,
      is_public: true,
      created_by: 'drag_drop',
    };
    
    createTreeMutation.mutate(newTreeData);
  }, [createTreeMutation]);

  return (
    <div className={styles.mainContainer}>
      {/* Full-screen Map Background */}
      <div className={styles.mapBackground}>
        <MapContainer 
          layers={layers} 
          communityTrees={communityTreesData || []} 
          onTreeDrop={handleTreeDrop}
          selectedAddress={selectedAddress}
        />
      </div>


      {/* Layer Controls Panel */}
      <LayerPanel
        layers={layers}
        onLayerToggle={handleLayerToggle}
        onVisibilityToggle={handleVisibilityToggle}
        onFilterChange={handleFilterChange}
        onTransparencyChange={handleTransparencyChange}
        onLayerReorder={handleLayerReorder}
        onAddressSelect={handleAddressSelect}
      />

      {/* Tree Species Carousel */}
      <TreeCarousel
        treeSpecies={treeSpecies}
        onTreeSelect={handleTreeSelect}
        onTreeDrag={handleTreeDrag}
      />
      

      {/* Tree creation status */}
      {createTreeMutation.isPending && (
        <div className="fixed top-4 right-4 z-50 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <FaSeedling /> Planting tree...
        </div>
      )}
      {createTreeMutation.isSuccess && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <FaTree /> Tree planted successfully!
        </div>
      )}
      {createTreeMutation.isError && (
        <div className="fixed top-4 right-4 z-50 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <FaTimes /> Failed to plant tree
        </div>
      )}
    </div>
  );
};
